import React from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { Navigate, useNavigate } from 'react-router-dom';
import globals from './globals';
import heroImage from './assets/recovery-cloud-hero.png';

async function loginUser(username, password) {
  const response = await fetch(`${globals.api_url}/account/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Login failed');
  return body;
}

const LoginPage = ({ token, setToken }) => {
  const navigate = useNavigate();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  if (token?.id) return <Navigate replace to="/" />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    sessionStorage.removeItem('token');
    setToken(null);

    try {
      const nextToken = await loginUser(username, password);
      sessionStorage.setItem('token', JSON.stringify(nextToken));
      setToken(nextToken);
      navigate('/');
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="login-shell" style={{ '--login-hero-image': `url(${heroImage})` }}>
      <Container maxWidth="lg">
        <Box className="login-layout">
          <Box className="login-brand">
            <Box className="brand-mark">
              <ShieldOutlinedIcon />
            </Box>
            <Typography className="brand-kicker" variant="overline">
              XOFTTEL Vault
            </Typography>
            <Typography variant="h4" component="h1">
              XOFTTEL Recovery Cloud
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Remote whole-computer backup management for workstations, laptops, datacenter servers, and recovery targets.
            </Typography>
            <Stack className="login-points" spacing={1.5}>
              <Box><StorageOutlinedIcon /> Image-backed OS and data snapshots</Box>
              <Box><SyncOutlinedIcon /> VM-assisted backup and restore workflow</Box>
              <Box><ShieldOutlinedIcon /> Offsite recovery when hardware is lost or failed</Box>
            </Stack>
          </Box>

          <Paper className="login-panel" elevation={0}>
            <Typography variant="h5">Sign in</Typography>
            <Typography variant="body2" color="text.secondary">
              Use the seeded compose account or an account already present in MongoDB.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.25}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Username"
                  name="username"
                  onChange={(event) => setUsername(event.target.value)}
                  required
                  value={username}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  type="password"
                  value={password}
                />
                <Button
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  {isSubmitting ? 'Signing in...' : 'Open console'}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
